import React, { useEffect, useState } from "react";
import { Card, Col, Container, Row } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import _ from "lodash";
import moment from "moment";
import vi from "moment/locale/vi";

import HeaderCreateItem from "components/Common/HeaderCreateItem";
import httpService from "services/httpService";
import useGetFormSchema from "custom-hook/useGetFormSchema";
import ViewableCommon from "components/Common/ViewableCommon";
import AccountInformation from "./AccountInformation";
import WalletInformation from "./WalletInformation";
import TransactionTable from "./TransactionTable";
import ContractTable from "./ContractTable";
import {
  toastrCRUDSuccess,
  toastrErrorAlert,
  toastrSuccessAlert,
} from "components/Common/AlertToastr";
import InputField from "components/form-control/InputField";
import { CommonText } from "components/Common/TextCommon";
import { CommonButton } from "components/Common/ButtonCommon";
import { TEXT_POST, TEXT_PUT } from "helpers/name_helper";
import InvitedInformation from "./InvitedInformation";
import { checkIsJSON, splitFullName } from "helpers/erp_helper";
import VVSTable from "components/form-control/VVSTable";
import { language_vn } from "helpers/language_vn";
import axios from "axios";

const NewCustomer = () => {
  const [customerAccount, setCustomerAccount] = useState(null);
  const [invitedCustomer, setInvitedCustomer] = useState(0);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [isCallLoading, setIsCallLoading] = useState(false);

  const { id } = useParams();
  const history = useHistory();
  const language = language_vn;

  useEffect(() => {
    getCustomerDetail();
  }, [id]);

  const schema = yup
    .object({
      fullName: yup.string().required("This field is required!"),
      email: yup.string().email("Email is invalid!"),
      phone: !id && yup.string().required("This field is required!"),
      birthday: yup
        .object()
        .nullable()
        .shape({
          __type: yup.string(),
          iso: yup.string(),
        })
        .transform((_, val) => (val ? { __type: "Date", iso: val } : null)),
      salesStaff: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "Employee",
                objectId: val.objectId,
              }
            : null
        ),
      salesTeam: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "EmployeeGroup",
                objectId: val.objectId,
              }
            : null
        ),
      group: yup
        .object()
        .nullable()
        .transform((_, val) =>
          val?.objectId
            ? {
                __type: "Pointer",
                className: "CustomerGroup",
                objectId: val.objectId,
              }
            : undefined
        ),
    })
    .required();

  const {
    handleSubmit,
    formState: { errors },
    ...rest
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { renderForm, getAddress } = useGetFormSchema(
    "Customer",
    yup,
    rest,
    errors
  );

  const getCustomerDetail = async () => {
    if (!id) return;

    const customerUrl = `/parse/classes/Customer/${id}?include=["invitedBy","salesStaff","user", "group", "salesTeam", "invitedBy"]`;
    const res = await httpService.get(customerUrl);
    const kycInformationUrl = `/parse/classes/KycInformation?where={"customer":{"__type":"Pointer", "className":"Customer", "objectId":"${id}"}}`;
    const kycInfoResponse = (await httpService.get(kycInformationUrl))
      .results[0];
    const accountsUrl = `/parse/classes/Account?where={"customer":{"__type": "Pointer", "className": "Customer", "objectId": "${id}"}}&include=["bank"]`;
    const accountsResponse = await httpService.get(accountsUrl);

    const isJSON = checkIsJSON(res?.addressLine1);
    const addressParseObj = isJSON ? JSON.parse(res.addressLine1) : null;
    const { reset } = rest;

    reset({
      ...res,
      birthday: res?.birthday
        ? moment(res?.birthday?.iso).utc().format("YYYY-MM-DD")
        : "",
      CCCDIssueDate: kycInfoResponse?.CCCDIssueDate
        ? moment(kycInfoResponse?.CCCDIssueDate?.iso).utc().format("DD/MM/YYYY")
        : "",
      CCCDNumber: kycInfoResponse?.CCCDNumber || "",
      CCCDFront: kycInfoResponse?.CCCDFront,
      CCCDBack: kycInfoResponse?.CCCDBack,
      CCCDIssuePlace: kycInfoResponse?.CCCDIssuePlace,
      address: addressParseObj?.addr || res?.addressLine2 || "",
      salesStaff: res?.salesStaff
        ? {
            text: res.salesStaff.fullName,
            objectId: res.salesStaff.objectId,
            className: "Employee",
            __type: "Pointer",
          }
        : { text: "" },

      salesTeam: res?.salesTeam
        ? {
            text: res.salesTeam?.name,
            objectId: res.salesTeam.objectId,
            className: "EmployeeGroup",
            __type: "Pointer",
          }
        : { text: "" },
      group: res?.group
        ? {
            text: res.group.name,
            objectId: res.group.objectId,
            className: "CustomerGroup",
            __type: "Pointer",
          }
        : { text: "" },
      isActive:
        res?.user?.status === "active" || res?.user?.status === "verifying",
      isVerified:
        res?.user?.status === "verifying" || res?.user?.status === "verified",

      invitedBy: res?.invitedBy
        ? {
            text: res.invitedBy.fullName,
            objectId: res.invitedBy.objectId,
            className: "Customer",
            __type: "Pointer",
          }
        : { text: "" },
    });
    getAddress({
      ...res,
      province: addressParseObj?.city || "",
      district: addressParseObj?.district || "",
      ward: addressParseObj?.ward || "",
    });
    setUserDetail(res.user);
    setCustomerDetail(res);

    if (!accountsResponse.results.length) return;
    setCustomerAccount(accountsResponse.results);

    const invitedCustomerUrl = `/parse/classes/Customer?where={"invitedBy":{"__type":"Pointer","className":"Customer","objectId":"${res.objectId}"}}`;
    const invitedCustomer = (await httpService.get(invitedCustomerUrl)).results;
    setInvitedCustomer(invitedCustomer.length);
  };

  const handleCallCustomer = async () => {
    try {
      setIsCallLoading(true);
      const currUserUrl = "/parse/users/me";
      const { objectId: UOI } = await httpService.get(currUserUrl);
      const currEmployeeUrl = `/parse/classes/Employee?where={"user":{"__type":"Pointer","className":"_User","objectId":"${UOI}"}}`;
      const currEmployee = (await httpService.get(currEmployeeUrl)).results[0];
      const customerPhone = rest.getValues("phone");

      if (!currEmployee) {
        toastrErrorAlert("User không phải là nhân viên Sale!");
        return;
      }
      if (!currEmployee?.extension) {
        toastrErrorAlert("Nhân viên chưa có extension!");
        return;
      }
      if (!customerPhone) {
        toastrErrorAlert("Vui lòng cập nhật số điện thoại khách hàng!");
        return;
      }

      const callUrl = "https://api.cloudfone.vn/api/CloudFone/AutoCallV2";
      await axios.post(callUrl, {
        ServiceName: `${process.env.CALL_CENTER_SERVICE_NAME}`,
        AuthUser: `${process.env.AUTH_USER}`,
        AuthKey: `${process.env.AUTH_KEY}`,
        Ext: currEmployee.extension,
        PhoneNumber: `${process.env.PHONE_NUMBER}/${leadPhone}`,
      });
      setIsCallLoading(false);
      toastrSuccessAlert(
        "Cuộc gọi đã được thiết lập, vui lòng chú ý phần mềm tổng đài!"
      );
    } catch (errorRes) {
      console.log("errorRes", errorRes.response.data);
      setIsCallLoading(false);
    }
  };

  const onSubmit = async values => {
    const excludeFields = [
      "createdAt",
      "updatedAt",
      "password",
      "username",
      "CCCDIssueDate",
      "CCCDNumber",
      "addressLine1",
      "code",
      "isVerified",
      "user",
      "accounts",
      "invitedBy",
    ];
    const customerValues = _.omit(values, excludeFields);

    const { address, username, password } = values;
    // const addressLine2 = address;
    const jsonAddress = JSON.stringify({
      city: values.province,
      district: values.district,
      ward: values.ward,
      addr: values.address,
    });

    const customer = { ...customerValues, addressLine1: jsonAddress };

    const userInfo = {
      ...splitFullName(customer.fullName),
      status: "inactive",
      fullName: customer.fullName,
      // email: customer.email,
      phone: customer.phone,
      username,
      password,
    };

    if (id) {
      try {
        const customerUrl = `/parse/classes/Customer/${id}`;
        await httpService.put(customerUrl, customer);
        toastrCRUDSuccess("Customer", TEXT_PUT);
      } catch (errorRes) {
        if (errorRes?.response?.data?.error) {
          toastrErrorAlert(errorRes.response?.data.error);
        }
      }
      return;
    }

    if (username && password) {
      try {
        const { objectId } = await httpService.post("/parse/users", userInfo);
        const customerUrl = `/parse/classes/Customer?where={"user":{"__type":"Pointer","className":"_User","objectId":"${objectId}"}}`;
        const COI = (await httpService.get(customerUrl)).results[0].objectId;
        const updateCustomerUrl = `/parse/classes/Customer/${COI}`;
        await httpService.put(updateCustomerUrl, customer);
        toastrCRUDSuccess("Customer", TEXT_POST);
        history.replace(`/customer/${COI}`);
      } catch (errorRes) {
        if (errorRes?.response?.data?.error) {
          toastrErrorAlert(errorRes.response?.data.error);
        }
      }
      return;
    }

    try {
      const customerUrl = "/parse/classes/Customer";
      const { objectId: COI } = await httpService.post(customerUrl, customer);
      toastrCRUDSuccess("Customer", TEXT_POST);
      history.replace(`/customer/${COI}`);
    } catch (errorRes) {
      if (errorRes?.response?.data?.error) {
        toastrErrorAlert(errorRes.response?.data.error);
      }
    }
  };

  const includeProps = {
    salesTeam: {
      conditionField: {
        isActive: true,
      },
    },
  };

  return (
    <div className="page-content">
      <Container fluid>
        <form onSubmit={handleSubmit(onSubmit)}>
          <HeaderCreateItem
            title={id ? "Chi tiết khách hàng" : "Thêm khách hàng"}
          >
            <div className="d-flex" style={{ gap: 12 }}>
              <ViewableCommon
                if={() => id}
                caseTrue={
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={handleCallCustomer}
                    disabled={isCallLoading}
                  >
                    <i
                      className={`${
                        isCallLoading
                          ? "bx bx-loader bx-spin"
                          : "fas fa-phone-alt"
                      } font-size-16 align-middle me-2`}
                    />
                    <span>Gọi</span>
                  </button>
                }
              />
              <CommonButton level={0}>Lưu</CommonButton>
            </div>
          </HeaderCreateItem>
          {renderForm([], includeProps)}
        </form>

        <ViewableCommon
          if={() => !id}
          caseTrue={
            <Card body>
              <CommonText level={1} className="m-0">
                Thông tin tài khoản
              </CommonText>
              <Row>
                <Col>
                  <InputField
                    label="Username"
                    name="username"
                    errors={errors}
                    {...rest}
                  />
                </Col>
                <Col>
                  <InputField
                    label="Password"
                    name="password"
                    errors={errors}
                    {...rest}
                  />
                </Col>
              </Row>
            </Card>
          }
        />
        <ViewableCommon
          if={() => id && customerAccount}
          caseTrue={
            <WalletInformation
              customerAccount={customerAccount}
              customerId={id}
            />
          }
        />
        <ViewableCommon
          if={() => id && userDetail}
          caseTrue={<AccountInformation userDetail={userDetail} />}
        />
        <ViewableCommon
          if={() => id}
          caseTrue={
            <InvitedInformation
              customerData={customerDetail}
              invitedCustomer={invitedCustomer}
            />
          }
        />
        <ViewableCommon if={() => id} caseTrue={<ContractTable id={id} />} />
        <ViewableCommon if={() => id} caseTrue={<TransactionTable id={id} />} />
        <ViewableCommon
          if={() => id}
          caseTrue={
            <Card body>
              <CommonText level={1} className="m-0">
                {language.historyCall}
              </CommonText>
              <VVSTable
                name="HistoryCallLog"
                disableAdd
                disableDelete
                disableSearch
                whereQuery={{
                  phone: customerDetail?.phone || 0,
                }}
                className="m-0 p-0 shadow-none"
              />
            </Card>
          }
        />
      </Container>
    </div>
  );
};

export default NewCustomer;
